/**
 * ADR-0032: newer versions of the Omni phone app. The back office publishes them; the phone
 * checks the server, shows what's new, and installs the APK itself (native OmniUpdate plugin:
 * download, check the SHA-256, hand it to Android's installer). On the web: a download link.
 */
import { registerPlugin, type PluginListenerHandle } from "@capacitor/core";

import { api, ApiError, apiBase } from "./api";
import { isNative, platform } from "./platform";
import { load, save } from "./storage";

export interface Release {
  id: string;
  platform: "android";
  version: string;
  build: number;
  notes: string;
  size: number;
  sha256: string;
  required: boolean;
  published_at: string | null;
  download_url: string;
  filename: string;
  /** With `build`: a newer version than this phone's exists */
  update?: boolean;
}

interface OmniUpdatePlugin {
  canInstall(): Promise<{ allowed: boolean }>;
  openInstallSettings(): Promise<void>;
  install(o: { url: string; sha256: string }): Promise<void>;
  addListener(event: "progress", fn: (p: { received: number; total: number }) => void): Promise<PluginListenerHandle>;
}

const Native = registerPlugin<OmniUpdatePlugin>("OmniUpdate");

export interface Installed { version: string; build: number }

/** The version on this phone (null on the web). */
export async function installedVersion(): Promise<Installed | null> {
  if (!isNative) return null;
  const { App } = await import("@capacitor/app");
  const info = await App.getInfo();
  return { version: info.version, build: Number(info.build) || 0 };
}

/** The newest published version; on the phone, with whether it is newer / required. */
export async function latestRelease(): Promise<Release | null> {
  const mine = await installedVersion();
  try {
    return await api<Release>("/api/app/releases/latest", { auth: false, query: { platform: "android", build: mine?.build } });
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export const absoluteUrl = (path: string) => `${apiBase()}${path}`;

/** Download and install (the phone asks the person to confirm). */
export async function installRelease(r: Release, onProgress?: (pct: number) => void): Promise<void> {
  if (!isNative || platform !== "android") throw new Error("Updates install on the Android app");
  const h = onProgress ? await Native.addListener("progress", (p) => p.total > 0 && onProgress(Math.round((p.received / p.total) * 100))) : null;
  try {
    await Native.install({ url: absoluteUrl(r.download_url), sha256: r.sha256 });
  } finally {
    await h?.remove();
  }
}

/** "Later" hides the banner for that version (not for a required one). */
export const dismissedBuild = () => load<number>("omni.update.dismissed", 0);
export const dismissBuild = (build: number) => save("omni.update.dismissed", build);

export function sizeLabel(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
